import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';

type Language = 'en' | 'ar';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
}

// Translation object
const translations: Record<Language, Record<string, string>> = {
  en: {
    // Navigation
    'nav.home': 'Home',
    'nav.booking': 'Booking',
    'nav.about': 'About',
    'nav.blog': 'Blog',
    
    // Hero Section
    'hero.title': 'Your Trusted Partner for Saudi Arabia Tours',
    'hero.subtitle': 'Umrah, Hajj, Ziyarat & Vehicle Booking Services',
    'hero.description': 'Experience the spiritual journey of a lifetime. We provide comprehensive tour services including Umrah and Hajj packages, Ziyarat tours, vehicle rentals (GMC Coasters), and expert guides across all cities of Saudi Arabia.',
    'hero.cta': 'Book Now',
    'hero.explore': 'Our Services',
    
    // Features
    'features.title': 'Why Choose Us',
    'features.subtitle': 'Your trusted partner for spiritual journeys in Saudi Arabia',
    'feature.1.title': 'Expert Guides',
    'feature.1.desc': 'Experienced guides for Umrah, Hajj, and Ziyarat tours',
    'feature.2.title': '24/7 Support',
    'feature.2.desc': 'Round-the-clock assistance throughout your journey',
    'feature.3.title': 'Premium Vehicles',
    'feature.3.desc': 'Comfortable GMC Coasters and modern fleet',
    'feature.4.title': 'Complete Packages',
    'feature.4.desc': 'All-inclusive Umrah and Hajj packages with accommodation',
    
    // Destinations
    'destinations.title': 'Explore Saudi Arabia',
    'destinations.subtitle': 'Tours across all major cities of the Kingdom',
    'destination.1.name': 'Makkah (Mecca)',
    'destination.1.desc': 'The Holiest City - Umrah & Hajj',
    'destination.2.name': 'Madinah (Medina)',
    'destination.2.desc': 'City of the Prophet - Ziyarat Tours',
    'destination.3.name': 'Riyadh',
    'destination.3.desc': 'Capital City - Cultural Tours',
    'destination.4.name': 'Jeddah',
    'destination.4.desc': 'Gateway to the Holy Cities',
    'destination.5.name': 'Taif',
    'destination.5.desc': 'Mountain City - Scenic Tours',
    'destination.6.name': 'Abha',
    'destination.6.desc': 'Asir Region - Natural Beauty',
    
    // Testimonials
    'testimonials.title': 'What Our Customers Say',
    'testimonials.subtitle': 'Real experiences from real travelers',
    'testimonial.1.name': 'Sarah Johnson',
    'testimonial.1.text': 'Amazing experience! The tour was well organized and the guides were knowledgeable.',
    'testimonial.2.name': 'Ahmed Al-Mansouri',
    'testimonial.2.text': 'Best travel agency I\'ve ever used. Highly recommended!',
    'testimonial.3.name': 'Emily Chen',
    'testimonial.3.text': 'Unforgettable memories. Everything was perfect from start to finish.',
    
    // CTA Section
    'cta.title': 'Ready to Start Your Journey?',
    'cta.subtitle': 'Book your dream vacation today and create memories that last forever',
    'cta.button': 'Get Started',
    
    // Footer
    'footer.description': 'Your trusted partner for Umrah, Hajj, Ziyarat, and tours across Saudi Arabia',
    'footer.quickLinks': 'Quick Links',
    'footer.contact': 'Contact Us',
    'footer.follow': 'Follow Us',
    'footer.rights': 'All rights reserved',
    
    // Statistics
    'stats.title': 'Our Achievements',
    'stats.subtitle': 'Serving pilgrims and tourists across Saudi Arabia',
    'stat.1.number': '50K+',
    'stat.1.label': 'Satisfied Pilgrims',
    'stat.2.number': '15+',
    'stat.2.label': 'Saudi Cities',
    'stat.3.number': '10K+',
    'stat.3.label': 'Umrah & Hajj Packages',
    'stat.4.number': '98%',
    'stat.4.label': 'Satisfaction Rate',
    
    // Services
    'services.title': 'Our Services',
    'services.subtitle': 'Complete travel solutions for Saudi Arabia',
    'service.1.title': 'Umrah Packages',
    'service.1.desc': 'Complete Umrah packages with accommodation, transportation, and expert guidance. Experience a blessed journey with our comprehensive services.',
    'service.2.title': 'Hajj Packages',
    'service.2.desc': 'Full Hajj packages with all arrangements including accommodation near Haram, transportation, and experienced guides for your spiritual journey.',
    'service.3.title': 'Ziyarat Tours',
    'service.3.desc': 'Visit historical and religious sites across Saudi Arabia. We provide guided Ziyarat tours to significant locations with detailed explanations.',
    'service.4.title': 'Vehicle Rental (GMC Coasters)',
    'service.4.desc': 'Rent comfortable GMC Coasters and other premium vehicles for group travel. Perfect for families and tour groups exploring Saudi Arabia.',
    'service.5.title': 'Tourist Guiding',
    'service.5.desc': 'Expert local guides for tours around all cities of Saudi Arabia. Knowledgeable guides fluent in multiple languages.',
    'service.6.title': 'City Tours',
    'service.6.desc': 'Explore all major cities including Riyadh, Jeddah, Taif, Abha, and more. Customized tours based on your preferences.',
    
    // Gallery
    'gallery.title': 'Travel Gallery',
    'gallery.subtitle': 'Beautiful moments from around the world',
    
    // Travel Tips
    'tips.title': 'Travel Tips & Guidelines',
    'tips.subtitle': 'Essential information for your journey to Saudi Arabia',
    'tip.1.title': 'Umrah & Hajj Preparation',
    'tip.1.desc': 'Important guidelines and requirements for performing Umrah and Hajj. Learn about Ihram, rituals, and best practices.',
    'tip.2.title': 'What to Pack',
    'tip.2.desc': 'Essential items for your spiritual journey including appropriate clothing, prayer items, and personal necessities.',
    'tip.3.title': 'Best Time to Visit',
    'tip.3.desc': 'Information about the best seasons for Umrah, Hajj, and visiting different cities in Saudi Arabia.',
    'tip.4.title': 'Cultural Guidelines',
    'tip.4.desc': 'Important cultural and religious guidelines to follow during your visit to ensure a respectful and meaningful experience.',
    
    // Newsletter
    'newsletter.title': 'Stay Updated',
    'newsletter.subtitle': 'Subscribe to our newsletter for exclusive deals and travel inspiration',
    'newsletter.placeholder': 'Enter your email address',
    'newsletter.button': 'Subscribe',
    'newsletter.success': 'Thank you for subscribing!',
    
    // Booking
    'booking.title': 'Book Your Trip',
    'booking.subtitle': 'Transportation Services',
    'booking.oneWay': 'One Way',
    'booking.roundTrip': 'Round Trip',
    'booking.hourly': 'Hourly',
    'booking.from': 'From',
    'booking.to': 'To',
    'booking.pickupTime': 'Pickup Time',
    'booking.dropoffTime': 'Drop-off Time',
    'booking.passengers': 'Passengers',
    'booking.vehicles': 'Vehicles',
    'booking.passengerCount': 'Passenger Count',
    'booking.vehicleCount': 'Vehicle Count',
    'booking.hours': 'Hours',
    'booking.search': 'Search',
    'booking.selectVehicle': 'Select Vehicle',
    'booking.ourFleet': 'Our Fleet',
    'booking.fleetDesc': 'A fleet that meets your needs',
    'booking.bookNow': 'Book Now',
    
    // About
    'about.title': 'About Us',
    'about.subtitle': 'Golden Elite for Travel Organization',
    'about.vision.title': 'Our Vision',
    'about.vision.desc': 'In pursuit of achieving Vision 2030 for sustainable development in the transportation and tourism sector, Golden Elite for Travel Organization was established to keep pace with progress and prosperity and contribute positively to this sector through innovation and trust in our wise leadership, striving for regional and Arab leadership.',
    'about.team.title': 'Our Team',
    'about.team.desc': 'Our team consists of multinational and multilingual professionals who possess knowledge and negotiation expertise to ensure the best prices for our clients. The team works around the clock to develop services using the latest technologies.',
    'about.mission.title': 'Our Mission',
    'about.mission.desc': 'Golden Elite for Travel Management – experience and professionalism in providing the best travel and tourism services within the Kingdom.',
    'about.values.title': 'Our Values',
    'about.value.1.title': 'Innovation',
    'about.value.1.desc': 'Continuously adopting the latest technologies and methods',
    'about.value.2.title': 'Trust',
    'about.value.2.desc': 'Building strong relationships based on trust and transparency',
    'about.value.3.title': 'Excellence',
    'about.value.3.desc': 'Striving for excellence in all our services',
    'about.value.4.title': 'Customer Focus',
    'about.value.4.desc': 'Putting our customers at the heart of everything we do',
    'about.contact.title': 'Contact Information',
    'about.taxId': 'Tax ID',
    'about.location': 'Location',
    'about.location.desc': 'Mecca - Al-Shawqiyah - Behind Al-Rabwa Hall',
    
    // Blog
    'blog.title': 'Blog',
    'blog.subtitle': 'Latest News & Travel Tips',
    'blog.readMore': 'Read More',
    'blog.readTime': 'min read',
    'blog.postedOn': 'Posted on',
    'blog.category': 'Category',
    'blog.search': 'Search articles...',
    'blog.allCategories': 'All Categories',
    'blog.noPosts': 'No posts found',
    'blog.categories.umrah': 'Umrah',
    'blog.categories.hajj': 'Hajj',
    'blog.categories.ziyarat': 'Ziyarat',
    'blog.categories.travel': 'Travel Tips',
    'blog.categories.vehicles': 'Vehicles',
    'blog.categories.guide': 'Guides',
  },
  ar: {
    // Navigation
    'nav.home': 'الرئيسية',
    'nav.booking': 'الحجز',
    'nav.about': 'من نحن',
    'nav.blog': 'المدونة',
    
    // Hero Section
    'hero.title': 'شريكك الموثوق لجولات المملكة العربية السعودية',
    'hero.subtitle': 'خدمات العمرة والحج والزيارات وحجز المركبات',
    'hero.description': 'اختبر رحلة روحية لا تُنسى. نقدم خدمات جولات شاملة تشمل باقات العمرة والحج، جولات الزيارات، تأجير المركبات (جمس كوستر)، ومرشدين خبراء في جميع مدن المملكة العربية السعودية.',
    'hero.cta': 'احجز الآن',
    'hero.explore': 'خدماتنا',
    
    // Features
    'features.title': 'لماذا تختارنا',
    'features.subtitle': 'شريكك الموثوق للرحلات الروحية في المملكة العربية السعودية',
    'feature.1.title': 'مرشدون خبراء',
    'feature.1.desc': 'مرشدون ذوو خبرة للعمرة والحج وجولات الزيارات',
    'feature.2.title': 'دعم على مدار الساعة',
    'feature.2.desc': 'مساعدة مستمرة طوال رحلتك',
    'feature.3.title': 'مركبات فاخرة',
    'feature.3.desc': 'جمس كوستر مريح وأسطول حديث',
    'feature.4.title': 'باقات شاملة',
    'feature.4.desc': 'باقات عمرة وحج شاملة مع الإقامة',
    
    // Destinations
    'destinations.title': 'استكشف المملكة العربية السعودية',
    'destinations.subtitle': 'جولات في جميع المدن الرئيسية في المملكة',
    'destination.1.name': 'مكة المكرمة',
    'destination.1.desc': 'أقدس مدينة - العمرة والحج',
    'destination.2.name': 'المدينة المنورة',
    'destination.2.desc': 'مدينة النبي - جولات الزيارات',
    'destination.3.name': 'الرياض',
    'destination.3.desc': 'العاصمة - الجولات الثقافية',
    'destination.4.name': 'جدة',
    'destination.4.desc': 'بوابة المدن المقدسة',
    'destination.5.name': 'الطائف',
    'destination.5.desc': 'مدينة الجبال - جولات خلابة',
    'destination.6.name': 'أبها',
    'destination.6.desc': 'منطقة عسير - الجمال الطبيعي',
    
    // Testimonials
    'testimonials.title': 'ماذا يقول عملاؤنا',
    'testimonials.subtitle': 'تجارب حقيقية من مسافرين حقيقيين',
    'testimonial.1.name': 'سارة جونسون',
    'testimonial.1.text': 'تجربة رائعة! كانت الجولة منظمة بشكل جيد والمرشدون كانوا على دراية.',
    'testimonial.2.name': 'أحمد المنصوري',
    'testimonial.2.text': 'أفضل وكالة سفر استخدمتها على الإطلاق. أنصح بها بشدة!',
    'testimonial.3.name': 'إيميلي تشن',
    'testimonial.3.text': 'ذكريات لا تُنسى. كل شيء كان مثالياً من البداية إلى النهاية.',
    
    // CTA Section
    'cta.title': 'مستعد لبدء رحلتك؟',
    'cta.subtitle': 'احجز إجازة أحلامك اليوم واصنع ذكريات تدوم إلى الأبد',
    'cta.button': 'ابدأ الآن',
    
    // Footer
    'footer.description': 'شريكك الموثوق للعمرة والحج والزيارات والجولات في جميع أنحاء المملكة العربية السعودية',
    'footer.quickLinks': 'روابط سريعة',
    'footer.contact': 'اتصل بنا',
    'footer.follow': 'تابعنا',
    'footer.rights': 'جميع الحقوق محفوظة',
    
    // Statistics
    'stats.title': 'إنجازاتنا',
    'stats.subtitle': 'خدمة الحجاج والسياح في جميع أنحاء المملكة العربية السعودية',
    'stat.1.number': '50 ألف+',
    'stat.1.label': 'حاج معجب',
    'stat.2.number': '15+',
    'stat.2.label': 'مدينة سعودية',
    'stat.3.number': '10 ألف+',
    'stat.3.label': 'باقة عمرة وحج',
    'stat.4.number': '98%',
    'stat.4.label': 'معدل الرضا',
    
    // Services
    'services.title': 'خدماتنا',
    'services.subtitle': 'حلول سفر شاملة للمملكة العربية السعودية',
    'service.1.title': 'باقات العمرة',
    'service.1.desc': 'باقات عمرة شاملة مع الإقامة والنقل والإرشاد المتخصص. اختبر رحلة مباركة مع خدماتنا الشاملة.',
    'service.2.title': 'باقات الحج',
    'service.2.desc': 'باقات حج كاملة مع جميع الترتيبات بما في ذلك الإقامة بالقرب من الحرم والنقل ومرشدين ذوي خبرة لرحلتك الروحية.',
    'service.3.title': 'جولات الزيارات',
    'service.3.desc': 'زيارة المواقع التاريخية والدينية في جميع أنحاء المملكة العربية السعودية. نقدم جولات زيارة موجهة للمواقع المهمة مع شرح مفصل.',
    'service.4.title': 'تأجير المركبات (جمس كوستر)',
    'service.4.desc': 'استأجر جمس كوستر مريح ومركبات فاخرة أخرى للسفر الجماعي. مثالي للعائلات ومجموعات الجولات لاستكشاف المملكة.',
    'service.5.title': 'الإرشاد السياحي',
    'service.5.desc': 'مرشدون محليون خبراء للجولات في جميع مدن المملكة العربية السعودية. مرشدون على دراية يتحدثون عدة لغات.',
    'service.6.title': 'جولات المدن',
    'service.6.desc': 'استكشف جميع المدن الرئيسية بما في ذلك الرياض وجدة والطائف وأبها والمزيد. جولات مخصصة حسب تفضيلاتك.',
    
    // Gallery
    'gallery.title': 'معرض السفر',
    'gallery.subtitle': 'لحظات جميلة من حول العالم',
    
    // Travel Tips
    'tips.title': 'نصائح وإرشادات السفر',
    'tips.subtitle': 'معلومات أساسية لرحلتك إلى المملكة العربية السعودية',
    'tip.1.title': 'التحضير للعمرة والحج',
    'tip.1.desc': 'إرشادات ومتطلبات مهمة لأداء العمرة والحج. تعرف على الإحرام والطقوس وأفضل الممارسات.',
    'tip.2.title': 'ما يجب حمله',
    'tip.2.desc': 'الأشياء الأساسية لرحلتك الروحية بما في ذلك الملابس المناسبة وأدوات الصلاة والضروريات الشخصية.',
    'tip.3.title': 'أفضل وقت للزيارة',
    'tip.3.desc': 'معلومات حول أفضل المواسم للعمرة والحج وزيارة المدن المختلفة في المملكة العربية السعودية.',
    'tip.4.title': 'الإرشادات الثقافية',
    'tip.4.desc': 'إرشادات ثقافية ودينية مهمة يجب اتباعها أثناء زيارتك لضمان تجربة محترمة وذات معنى.',
    
    // Newsletter
    'newsletter.title': 'ابق على اطلاع',
    'newsletter.subtitle': 'اشترك في نشرتنا الإخبارية للحصول على عروض حصرية وإلهام السفر',
    'newsletter.placeholder': 'أدخل عنوان بريدك الإلكتروني',
    'newsletter.button': 'اشترك',
    'newsletter.success': 'شكراً لك على الاشتراك!',
    
    // Booking
    'booking.title': 'احجز رحلتك',
    'booking.subtitle': 'خدمات التنقلات',
    'booking.oneWay': 'ذهاب',
    'booking.roundTrip': 'ذهاب وعودة',
    'booking.hourly': 'عدد الساعات',
    'booking.from': 'من',
    'booking.to': 'إلى',
    'booking.pickupTime': 'وقت الاستقبال',
    'booking.dropoffTime': 'وقت المغادرة',
    'booking.passengers': 'الضيوف',
    'booking.vehicles': 'السيارات',
    'booking.passengerCount': 'عدد الركاب',
    'booking.vehicleCount': 'عدد السيارات',
    'booking.hours': 'عدد الساعات',
    'booking.search': 'بحث',
    'booking.selectVehicle': 'اختر المركبة',
    'booking.ourFleet': 'أسطول يلبي احتياجاتك',
    'booking.fleetDesc': 'أسطول يلبي احتياجاتك',
    'booking.bookNow': 'احجز الآن',
    
    // About
    'about.title': 'من نحن',
    'about.subtitle': 'النخبة الذهبية لتنظيم الرحلات',
    'about.vision.title': 'رؤيتنا',
    'about.vision.desc': 'سعياً لتحقيق رؤية 2030 في الاستدامة التطويرية في قطاع النقل والمواصلات والسياحة، أُنشئت مؤسسة النخبة الذهبية لتنظيم الرحلات لمواكبة التقدم والازدهار والمساهمة بشكل إيجابي في هذا القطاع من خلال الابتكار والثقة في قيادتنا الرشيدة، والسعي للريادة إقليمياً وعربياً.',
    'about.team.title': 'فريق العمل',
    'about.team.desc': 'يتكون فريق العمل من محترفين متعدد الجنسيات واللغات، يمتلكون معرفة وخبرة تفاوضية لضمان تقديم أفضل الأسعار لعملائنا. كما يعمل الفريق على مدار الساعة لتطوير الخدمات باستخدام أحدث التقنيات.',
    'about.mission.title': 'مهمتنا',
    'about.mission.desc': 'النخبة الذهبية لإدارة الرحلات – خبرة واحترافية في تقديم أفضل خدمات السفر والسياحة داخل المملكة.',
    'about.values.title': 'قيمنا',
    'about.value.1.title': 'الابتكار',
    'about.value.1.desc': 'اعتماد أحدث التقنيات والأساليب بشكل مستمر',
    'about.value.2.title': 'الثقة',
    'about.value.2.desc': 'بناء علاقات قوية مبنية على الثقة والشفافية',
    'about.value.3.title': 'التميز',
    'about.value.3.desc': 'السعي للتميز في جميع خدماتنا',
    'about.value.4.title': 'التركيز على العميل',
    'about.value.4.desc': 'وضع عملائنا في قلب كل ما نقوم به',
    'about.contact.title': 'معلومات الاتصال',
    'about.taxId': 'الرقم الضريبي',
    'about.location': 'موقعنا',
    'about.location.desc': 'مكة - حي الشوقية - خلف قاعة الربوة',
    
    // Blog
    'blog.title': 'المدونة',
    'blog.subtitle': 'أحدث الأخبار ونصائح السفر',
    'blog.readMore': 'اقرأ المزيد',
    'blog.readTime': 'دقيقة قراءة',
    'blog.postedOn': 'نشر في',
    'blog.category': 'الفئة',
    'blog.search': 'ابحث عن المقالات...',
    'blog.allCategories': 'جميع الفئات',
    'blog.noPosts': 'لم يتم العثور على مقالات',
    'blog.categories.umrah': 'العمرة',
    'blog.categories.hajj': 'الحج',
    'blog.categories.ziyarat': 'الزيارات',
    'blog.categories.travel': 'نصائح السفر',
    'blog.categories.vehicles': 'المركبات',
    'blog.categories.guide': 'دلائل',
  },
};

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const savedLang = localStorage.getItem('language') as Language;
    return savedLang || 'en';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

